import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CartContext, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Calendar, Clock, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cart, clearCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    pickup_date: '',
    pickup_time: '',
    notes: ''
  });

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const hasProductsWith24hNotice = cart.some(item => item.requires_24h_notice);

  // Générer les créneaux horaires en fonction du jour sélectionné
  const getAvailableTimes = (selectedDate) => {
    if (!selectedDate) return [];
    
    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay(); // 0 = Dimanche, 1 = Lundi, etc.
    
    let startTime, endTime;
    
    // Définir les horaires selon le jour (1h après ouverture, 1h avant fermeture)
    if (dayOfWeek === 0) {
      // Dimanche : 9h-16h → Retrait 10h-15h
      startTime = 10;
      endTime = 15;
    } else if (dayOfWeek >= 2 && dayOfWeek <= 4) {
      // Mardi-Jeudi : 9h-14h → Retrait 10h-13h
      startTime = 10;
      endTime = 13;
    } else if (dayOfWeek === 5 || dayOfWeek === 6) {
      // Vendredi-Samedi : 9h-19h → Retrait 10h-18h
      startTime = 10;
      endTime = 18;
    } else {
      // Lundi : Fermé
      return [];
    }
    
    // Générer les créneaux de 30 minutes
    const times = [];
    for (let hour = startTime; hour <= endTime; hour++) {
      times.push(`${hour.toString().padStart(2, '0')}:00`);
      if (hour < endTime) {
        times.push(`${hour.toString().padStart(2, '0')}:30`);
      }
    }
    
    return times;
  };

  const availableTimes = getAvailableTimes(formData.pickup_date);

  const getMinDate = () => {
    const tomorrow = new Date();
    if (hasProductsWith24hNotice) {
      tomorrow.setDate(tomorrow.getDate() + 1);
    }
    return tomorrow.toISOString().split('T')[0];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price
        })),
        pickup_date: formData.pickup_date,
        pickup_time: formData.pickup_time,
        notes: formData.notes
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const orderId = orderResponse.data.id;

      const paymentResponse = await axios.post(
        `${API}/payment/create-checkout-session?order_id=${orderId}&origin_url=${window.location.origin}`,
        {}
      );

      window.location.href = paymentResponse.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la commande');
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        <h1 className="text-4xl font-bold mb-8 text-gray-900" data-testid="checkout-title">Finaliser la commande</h1>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-sm p-8 border border-gray-100">
            <h2 className="text-2xl font-bold mb-6 text-gray-900">Informations de retrait</h2>

            {hasProductsWith24hNotice && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start space-x-3">
                <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                <p className="text-amber-900 text-sm font-medium">
                  Votre panier contient des produits nécessitant une commande 24h à l'avance.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-3">
                  <Calendar size={18} />
                  <span>Date de retrait</span>
                </label>
                <input
                  type="date"
                  required
                  min={getMinDate()}
                  value={formData.pickup_date}
                  onChange={(e) => setFormData({ ...formData, pickup_date: e.target.value, pickup_time: '' })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-800 focus:border-transparent transition"
                  data-testid="pickup-date"
                />
              </div>

              <div>
                <label className="flex items-center space-x-2 text-sm font-semibold text-gray-700 mb-3">
                  <Clock size={18} />
                  <span>Heure de retrait</span>
                </label>
                {!formData.pickup_date ? (
                  <div className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 text-gray-500 text-sm">
                    Veuillez d'abord sélectionner une date
                  </div>
                ) : availableTimes.length === 0 ? (
                  <div className="w-full px-4 py-3 border border-red-200 rounded-xl bg-red-50 text-red-600 text-sm">
                    ⚠️ Fermé ce jour-là (Lundi fermé)
                  </div>
                ) : (
                  <select
                    required
                    value={formData.pickup_time}
                    onChange={(e) => setFormData({ ...formData, pickup_time: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-800 focus:border-transparent transition"
                    data-testid="pickup-time"
                  >
                    <option value="">Sélectionner une heure</option>
                    {availableTimes.map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Notes (optionnel)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-slate-800 focus:border-transparent transition"
                  placeholder="Informations complémentaires..."
                  data-testid="order-notes"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-slate-800 to-slate-900 text-white py-4 rounded-xl font-semibold text-lg hover:from-slate-900 hover:to-black transition shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                data-testid="proceed-payment-btn"
              >
                {loading ? 'Traitement...' : 'Procéder au paiement'}
              </button>
            </form>
          </div>

          <div>
            <div className="bg-white rounded-2xl shadow-sm p-8 mb-6 border border-gray-100">
              <h2 className="text-2xl font-bold mb-6 text-gray-900">Récapitulatif</h2>
              
              <div className="space-y-4 mb-6 pb-6 border-b border-gray-100">
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between" data-testid={`summary-item-${item.id}`}>
                    <span className="text-gray-700">
                      {item.name} <span className="text-gray-500">× {item.quantity}</span>
                    </span>
                    <span className="font-semibold text-gray-900">{(item.price * item.quantity).toFixed(2)}€</span>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center">
                <span className="text-xl font-semibold text-gray-700">Total:</span>
                <span className="text-3xl font-bold text-gray-900" data-testid="checkout-total">{total.toFixed(2)}€</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-2xl p-6 border border-gray-200">
              <h3 className="font-bold mb-4 text-gray-900">Vos informations</h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p><span className="font-semibold">Nom:</span> {user.full_name}</p>
                <p><span className="font-semibold">Email:</span> {user.email}</p>
                <p><span className="font-semibold">Téléphone:</span> {user.phone}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;